import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { isEmpty, isEqual } from 'lodash-es';
import { combineLatest, Observable, pairwise, startWith, Subject, Subscription, takeUntil } from 'rxjs';
import { PatientInsurance, ProductGroupHcpcs, ReorderDetails } from 'src/app/core/models';
import { ComponentCanDeactivate } from '../core/services/component-can-deactivate';
import { AppState } from '../core/store/app/app.state';
import { LookupState } from '../core/store/lookup/lookup.state';
import {
  EvaluateActiveIndexFromUrl,
  ResetOrderWizardNavigation,
  SetSkipBillingStep,
} from '../core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.actions';
import {
  LoadOrderWizard,
  ResetOrderWizardData,
} from '../core/store/order-wizard/order-wizard-data/order-wizard-data.actions';
import { PatientInsurancesState } from '../core/store/patient-insurances/patient-insurances.state';
import { CcsSentryError } from '../core/store/utils';
import { ResetOrderWizardSubmission } from '../core/store/order-wizard/order-wizard-submission/order-wizard-submission.actions';
import { OrderWizardDataState } from '../core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import { OrderWizardStatus } from '../core/store/order-wizard/order-wizard-data/order-wizard-data.model';
import { OrderWizardSubmissionState } from '../core/store/order-wizard/order-wizard-submission/order-wizard-submission.state';
import { WizardStep } from '../core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.model';
import { OrderWizardNavigationState } from '../core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.state';
import { UserState } from '../core/store/user/user.state';
import { PatientState } from '../core/store/patient/patient.state';

@Component({
  selector: 'app-order-wizard',
  templateUrl: './order-wizard.component.html',
  styleUrls: ['./order-wizard.component.scss'],
})
export class OrderWizardComponent extends ComponentCanDeactivate implements OnInit, OnDestroy {
  @Select(PatientState.selectedPatientId) selectedPatientId$!: Observable<number | null>;
  @Select(LookupState.hcpcsGroups) hcpcsGroups$!: Observable<ProductGroupHcpcs[]>;
  @Select(LookupState.hcpcsGroupsIsFinished) hcpcsGroupsIsFinished$!: Observable<boolean>;
  @Select(PatientInsurancesState.patientInsurance) patientInsurance$!: Observable<PatientInsurance | null>;
  @Select(PatientInsurancesState.isFinished) patientInsuranceIsFinished$!: Observable<boolean>;

  @Select(OrderWizardDataState.isLoading) isLoading$!: Observable<boolean>;
  @Select(OrderWizardDataState.errors) errors$!: Observable<CcsSentryError[] | null>;
  @Select(OrderWizardDataState.reorder) reorder$!: Observable<ReorderDetails | null>;
  @Select(OrderWizardDataState.computedTargetShipDate) computedTargetShipDate$!: Observable<string | null>;
  @Select(OrderWizardDataState.isShipmentDelayed) isShipmentDelayed$!: Observable<boolean>;
  @Select(OrderWizardDataState.isShippingSoon) isShippingSoon$!: Observable<boolean>;
  @Select(OrderWizardDataState.isInsuranceChanged) isInsuranceChanged$!: Observable<boolean>;
  @Select(OrderWizardDataState.isMissingPwoDocs) isMissingPwoDocs$!: Observable<boolean>;
  @Select(OrderWizardDataState.skipBillingStep) skipBillingStep$!: Observable<OrderWizardStatus>;

  @Select(OrderWizardNavigationState.activeIndex) activeIndex$!: Observable<number>;
  @Select(OrderWizardNavigationState.activeStep) activeStep$!: Observable<WizardStep>;
  @Select(OrderWizardNavigationState.paymentOptionalCorrectedIndex)
  paymentOptionalCorrectedIndex$!: Observable<number>;
  @Select(OrderWizardNavigationState.shouldSkipForOptionalPayment) shouldSkipForOptionalPayment$!: Observable<boolean>;
  @Select(OrderWizardNavigationState.steps) steps$!: Observable<WizardStep[]>;
  @Select(OrderWizardNavigationState.stepCount) stepCount$!: Observable<number>;
  @Select(OrderWizardNavigationState.lastConfirmedIndex) lastConfirmedIndex$!: Observable<number>;

  hasStartedLoading: boolean = false;
  navigationSubscription: Subscription | null = null;
  isPaymentRequiredOrder: boolean = false;

  constructor(private store: Store, private route: ActivatedRoute, private router: Router) {
    super();
    this.store.dispatch(new EvaluateActiveIndexFromUrl(this.router.url));
  }

  ngOnInit(): void {
    // Set payment required flag early based on URL
    this.isPaymentRequiredOrder = this.router.url.includes('/make-payment');

    const orderId = parseInt(this.route.snapshot.paramMap.get('orderId')!);

    if (this.isPaymentRequiredOrder) {
      this.loadOrderData(orderId);
      return;
    }

    const terminate = new Subject<void>();

    terminate.subscribe(() => (this.hasStartedLoading = true));

    combineLatest([
      this.selectedPatientId$,
      this.patientInsurance$,
      this.patientInsuranceIsFinished$,
      this.hcpcsGroups$,
      this.hcpcsGroupsIsFinished$,
    ])
      .pipe(takeUntil(terminate))
      .subscribe(
        ([selectedPatientId, patientInsurance, patientInsuranceIsFinished, hcpcsGroups, hcpcsGroupsIsFinished]) => {
          if (patientInsuranceIsFinished && hcpcsGroupsIsFinished) {
            if (selectedPatientId && patientInsurance && hcpcsGroups.length > 0) {
              this.loadOrderData(orderId);
            }
            terminate.next();
          }
        }
      );

    this.navigationSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.store.dispatch(new EvaluateActiveIndexFromUrl(this.router.url));
      }
    });

    this.skipBillingStep$.pipe(startWith({ value: false, debugInfo: null }), pairwise()).subscribe(([prev, next]) => {
      if (!isEqual(prev, next)) {
        this.store.dispatch(new SetSkipBillingStep(next.value));
      }
    });

    combineLatest([
      this.lastConfirmedIndex$,
      this.activeIndex$,
      this.steps$,
      this.shouldSkipForOptionalPayment$,
      this.skipBillingStep$,
    ]).subscribe(([lastConfirmedIndex, activeIndex, steps, shouldSkip, skipBillingStep]) => {
      const skippedKey = steps[lastConfirmedIndex + 1]?.key;
      const prevKey = steps[activeIndex - 1]?.key;
      // force navigation to first step as protective measure unless we skipped the billing step
      if (
        lastConfirmedIndex + 1 < activeIndex &&
        !(prevKey === skippedKey && skippedKey === 'billing' && (shouldSkip || skipBillingStep.value))
      ) {
        this.router.navigate([steps[0].url], { relativeTo: this.route.parent });
        return;
      }
    });
  }

  private loadOrderData(orderId: number): void {
    this.store.dispatch(new LoadOrderWizard(orderId));

    this.hasStartedLoading = true;

    // Set isPaymentRequiredOrder based on URL instead of order status
    this.isPaymentRequiredOrder = this.router.url.includes('/make-payment');
  }

  canDeactivate() {
    const exitOrderWizard = this.store.selectSnapshot(OrderWizardDataState.exitOrderWizard);
    const isRefreshByModalRequired = this.store.selectSnapshot(AppState.isRefreshByModalRequired);
    const isSessionExpired = this.store.selectSnapshot(UserState.isSessionExpired);
    const stepConfirmations = this.store.selectSnapshot(OrderWizardNavigationState.stepConfirmations);
    const submitReorder = this.store.selectSnapshot(OrderWizardSubmissionState.submitReorder);

    return (
      isEmpty(stepConfirmations) ||
      exitOrderWizard.value ||
      (submitReorder.isFinished && submitReorder.error === null) ||
      isSessionExpired ||
      isRefreshByModalRequired
    );
  }

  ngOnDestroy() {
    this.navigationSubscription?.unsubscribe();

    this.store.dispatch([
      new ResetOrderWizardData(),
      new ResetOrderWizardNavigation(),
      new ResetOrderWizardSubmission(),
    ]);
  }
}
